import { useHistory } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Autocomplete,
  Button,
  InputAdornment,
  TextField,
  createFilterOptions,
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import debounce from 'lodash/debounce';
import { fetchCompanies } from 'api/companies';
import actions from 'store/actions';
import styles from './home.module.scss';
import jlllogo from 'img/jlllogo.png';
import { save } from 'api/needsassessments';
import { useValidator } from 'hooks/useValidator';
import NewCompanyModal from './NewCompanyModal';
import { validationConfig } from 'config/validationConfig';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { ReactComponent as SupportIcon } from 'icons/life-buoy.svg';
import { useTranslation } from 'react-i18next';
import { weakKey } from 'utils/objects';
import paths from 'constants/paths';
import { getFullPathOrHome } from 'utils/paths';

const filter = createFilterOptions();

export default function Home() {
  const history = useHistory();
  const dispatch = useDispatch();
  const na = useSelector((state) => state.na);
  const { t } = useTranslation();

  const [projectInputDisabled, setProjectInputDisabled] = useState(true);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companiesList, setCompaniesList] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [company, setCompany] = useState();
  const [project, setProject] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [companyInput, setCompanyInput] = useState('');

  const handleModalAction = (confirmCancel) => {
    setOpenModal(false);
    setCompanyInput('');
    if (confirmCancel.confirm && confirmCancel.value !== '') {
      validator.reset();
      const customCompany = {
        name: confirmCancel.value,
        isEdpCompany: false,
        id: null,
      };
      onChangeSelectedCompany(customCompany);
    }
  };

  const handleCreateNewCompany = () => {
    setOpenModal(true);
  };

  const onChangeSelectedCompany = (newCompany) => {
    setCompany(newCompany);
    setProjectInputDisabled(!newCompany);
    if (!newCompany) setProject('');
  };

  const onInputChangeCompany = async (_, value) => {
    setCompaniesList([]);

    setCompaniesLoading(true);
    setOpenDropdown(true);
    const companies = await fetchCompanies(value);
    setCompaniesLoading(false);

    setCompaniesList(companies);
  };

  const onLibraryBtnClick = () => {
    history.push(getFullPathOrHome(paths.library));
  };

  const onSupportBtnClick = () => {
    history.push(getFullPathOrHome(paths.support));
  };

  const debouncedCompanySearch = useMemo(() => debounce(onInputChangeCompany, 300), []);

  const [validables, validator] = useValidator(['company', 'project'], () => {
    const message = {};
    if (!company) message.company = t('home.companyNameIsRequired');
    if (!project) message.project = t('home.projectNameIsRequired');

    return message;
  });

  const handleGetStarted = async () => {
    if (!validator.validate()) return;

    const savedNa = await save(company, project, na);

    dispatch(actions.na.updateId(savedNa.id));
    dispatch(actions.company.setCompany(company, project));
    dispatch(actions.na.setNA(savedNa));

    history.push(getFullPathOrHome(`${paths.step}${paths.steps.currentSituation}/${savedNa.id}`));
  };

  return (
    <div className={styles.container}>
      <div className={styles.column1}></div>
      <div className={styles.column2}>
        <div className={styles.column2__container}>
          <img className={styles.logo} alt="" src={jlllogo}></img>
          <div className={styles.title}>{t('home.title')}</div>
          <div className={styles.description}>{t('home.description')}</div>
          <div className={styles.companyname_title}>{t('home.companyNameInput')}</div>
          <Autocomplete
            data-testid="HomeCompanyNameInput"
            disablePortal
            forcePopupIcon={openDropdown}
            value={company || null}
            onChange={(_, newValue) => {
              if (newValue && newValue.inputValue) {
                handleCreateNewCompany();
              } else {
                onChangeSelectedCompany(newValue);
              }
            }}
            noOptionsText={''}
            loadingText={t('common.loading')}
            loading={companiesLoading}
            className={styles.companyname_input}
            options={companiesList}
            filterOptions={(options, state) => {
              const filtered = filter(options, state);
              if (
                !companiesLoading &&
                state.inputValue.length > validationConfig.minCompanySearchLength
              ) {
                filtered.unshift({
                  inputValue: state.inputValue,
                  name: t('home.createNew'),
                });
              }

              return filtered;
            }}
            open={openDropdown}
            onClose={() => setOpenDropdown(false)}
            inputValue={companyInput}
            onInputChange={(_, value, reason) => {
              setCompanyInput(value);

              if (reason === 'reset' || value.length <= validationConfig.minCompanySearchLength) {
                debouncedCompanySearch.cancel();
                setOpenDropdown(false);
                return;
              }

              return debouncedCompanySearch(_, value);
            }}
            isOptionEqualToValue={(option, value) => option && option.name === value.name}
            getOptionLabel={(option) => {
              if (option.inputValue) {
                return option.inputValue;
              }
              return option.name;
            }}
            renderInput={(params) => {
              params.InputProps.startAdornment = (
                <>
                  <InputAdornment position="start">
                    <SearchOutlinedIcon />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              );
              return (
                <TextField
                  {...params}
                  className={styles.input}
                  error={!validables.company.valid}
                  helperText={validables.company.message}
                  placeholder={t('home.companyNamePlaceholder')}
                />
              );
            }}
            renderOption={(props, option, { inputValue }) => {
              if (option.inputValue) {
                const newProps = { ...props, key: 'newCompany' };
                return (
                  <li {...newProps}>
                    <div>
                      <span>{option.name}</span>
                    </div>
                  </li>
                );
              }

              const parts = option.name
                .split(new RegExp('(' + inputValue + ')', 'i'))
                .map((part) => ({
                  text: part,
                  highlight: part.toLowerCase() === inputValue.toLowerCase(),
                }));

              return (
                <li {...props}>
                  <div>
                    {parts.map((part) => (
                      <span
                        key={weakKey(part)}
                        style={{
                          fontWeight: part.highlight ? 800 : 400,
                        }}>
                        {part.text}
                      </span>
                    ))}
                  </div>
                </li>
              );
            }}
          />
          <div className={styles.projectname_title}>{t('home.projectNameInput')}</div>
          <TextField
            data-testid="HomeProjectNameInput"
            required
            className={styles.projectname_input}
            disabled={projectInputDisabled}
            placeholder={t('home.projectNamePlaceholder')}
            onChange={(event) => setProject(event.target.value)}
            value={project}
            error={!validables.project.valid}
            helperText={validables.project.message}
          />
          <Button
            data-testid="HomeGetStartedButton"
            className={styles.getstarted_button}
            size="medium"
            variant="contained"
            onClick={handleGetStarted}
            disableRipple>
            {t('home.startBtn')}
          </Button>
        </div>
        <div className={styles.other_buttons}>
          <Button
            data-testid="HomeLibraryButton"
            className={styles.other_buttons__btn}
            size="small"
            variant="contained"
            onClick={onLibraryBtnClick}
            startIcon={<FormatListBulletedIcon />}
            endIcon={<ArrowForwardIosIcon />}>
            {t('home.library')}
          </Button>
          <Button
            data-testid="HomeSupportButton"
            className={styles.other_buttons__btn}
            size="small"
            variant="contained"
            onClick={onSupportBtnClick}
            startIcon={<SupportIcon />}
            endIcon={<ArrowForwardIosIcon />}>
            {t('home.support')}
          </Button>
        </div>
        <NewCompanyModal open={openModal} input={companyInput} onAction={handleModalAction} />
      </div>
    </div>
  );
}
